import React, { useEffect, useRef, useState } from 'react';
import { Modal, TextInput } from '@mantine/core';
import type { CibusVoidConfirmView } from 'shared-models/browser';
import { Button } from '../../../../../components/Button';
import { formatMoney } from '../../../../../utils/money';
import { noSelectionTextInputProps } from '../../receipt/receipt-input.utils';
import classes from './cibus-card-dialog.module.scss';

export type CibusCardSubmit = { code: string; cibusOrderId: string; confirmDealId?: string };

type CibusCardDialogProps = {
  isOpen: boolean;
  mode: 'charge' | 'void';
  amountLabel: string;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (payload: CibusCardSubmit) => Promise<boolean | CibusVoidConfirmView | undefined>;
};

// Cibus codes come from the diner's app, not a magstripe: alphanumeric, no track-2 parsing.
export function normalizeCibusCode(raw: string): string | null {
  const code = raw.trim().replace(/[^0-9A-Za-z]/g, '');
  return code.length >= 4 ? code.slice(0, 32) : null;
}

export const CibusCardDialog: React.FC<CibusCardDialogProps> = ({
  isOpen,
  mode,
  amountLabel,
  isSubmitting,
  onClose,
  onSubmit,
}) => {
  const [raw, setRaw] = useState('');
  const [cibusOrderId, setCibusOrderId] = useState('');
  const [voidConfirm, setVoidConfirm] = useState<CibusVoidConfirmView | null>(null);
  const submitLockRef = useRef(false);

  useEffect(() => {
    setRaw('');
    setCibusOrderId('');
    setVoidConfirm(null);
    submitLockRef.current = false;
  }, [isOpen]);

  useEffect(() => {
    if (!isSubmitting) {
      submitLockRef.current = false;
    }
  }, [isSubmitting]);

  const isVoid = mode === 'void';
  const code = normalizeCibusCode(raw);
  const canSubmit = !isSubmitting && (isVoid ? cibusOrderId.trim() !== '' : code !== null);

  const handleSubmit = async (confirmDealId?: string) => {
    if (!canSubmit || submitLockRef.current) {
      return;
    }
    submitLockRef.current = true;
    const result = await onSubmit({
      code: code ?? '',
      cibusOrderId: isVoid ? cibusOrderId.trim() : '',
      confirmDealId,
    });
    setVoidConfirm(typeof result === 'object' ? result : null);
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      centered
      dir="rtl"
      radius="lg"
      size={420}
      title={isVoid ? 'ביטול עסקת סיבוס' : 'תשלום סיבוס'}
      closeButtonProps={{ 'data-testid': 'cibus-card-close' } as Record<string, string>}
    >
      <div className={classes.body} data-testid="cibus-card-dialog">
        {voidConfirm ? (
          <>
            <p className={classes.hint}>
              {`עסקה ${cibusOrderId.trim()} שייכת להזמנה ${voidConfirm.orderCode ?? ''}, `}
              {voidConfirm.cardLast4 ? `כרטיס ***${voidConfirm.cardLast4}, ` : ''}
              {`₪${formatMoney(voidConfirm.amount)} — לבטל?`}
            </p>
            <div className={classes.buttonRow}>
              <Button
                variant="cancel"
                onClick={() => { void handleSubmit(cibusOrderId.trim()); }}
                disabled={!canSubmit}
                loading={isSubmitting}
                loadingLabel="מבטל..."
                dataTestId="cibus-void-confirm"
              >
                בטל עסקה
              </Button>
              <Button
                variant="cancel"
                className="btn-bordered"
                onClick={() => setVoidConfirm(null)}
                disabled={isSubmitting}
                dataTestId="cibus-void-back"
              >
                חזור
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className={classes.hint}>
              {isVoid ? 'סיבוס מבטל את העסקה במלואה' : 'הקלד את הקוד החד-פעמי מאפליקציית סיבוס'}
            </p>
            {isVoid ? (
              <TextInput
                label="מספר עסקה סיבוס"
                data-testid="cibus-card-deal-id"
                data-autofocus
                autoComplete="off"
                {...noSelectionTextInputProps}
                value={cibusOrderId}
                onChange={(event) => setCibusOrderId(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void handleSubmit();
                  }
                }}
              />
            ) : (
              <TextInput
                label="קוד סיבוס"
                data-testid="cibus-card-code"
                data-autofocus
                autoComplete="off"
                {...noSelectionTextInputProps}
                value={raw}
                onChange={(event) => setRaw(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void handleSubmit();
                  }
                }}
              />
            )}
            <div className={classes.amountRow}>
              <span className={classes.amountLabel}>{isVoid ? 'סכום לביטול' : 'סכום לחיוב'}</span>
              <span dir="ltr">{amountLabel}</span>
            </div>
            <div className={classes.buttonRow}>
              <Button variant="cancel" onClick={onClose} disabled={isSubmitting} dataTestId="cibus-card-cancel">
                ביטול
              </Button>
              <Button
                variant="confirm"
                onClick={() => { void handleSubmit(); }}
                disabled={!canSubmit}
                loading={isSubmitting}
                loadingLabel={isVoid ? 'מבטל...' : 'מחייב...'}
                dataTestId="cibus-card-submit"
              >
                {isVoid ? 'בטל עסקה' : 'אישור'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
